import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "redux/store";

// Use throughout your app instead of plain `useDispatch` and `useSelector`
type DispatchFunc = () => AppDispatch
export const useAppDispatch: DispatchFunc = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Theme color palette type
export interface ThemeColors {
    // Backgrounds
    backgroundColor: string;
    backgroundColorSecondary: string;
    backgroundColorTertiary: string;
    // Text
    textColor: string;
    textColorSecondary: string;
    textColorMuted: string;
    // Borders
    borderColor: string;
    borderColorSecondary: string;
    // Primary colors
    primaryColor: string;
    primaryColorHover: string;
    primaryColorLight: string;
    // Interactive
    hoverBackground: string;
    selectedBackground: string;
    // Special
    iconColor: string;
    placeholderColor: string;
}

// Theme color definitions
const themeColorPalettes: Record<string, ThemeColors> = {
    // Sentier Light (default light theme)
    light: {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#E9E9E9",
        backgroundColorTertiary: "#f5f5f5",
        textColor: "#333",
        textColorSecondary: "#666",
        textColorMuted: "#999",
        borderColor: "#A0A0A0",
        borderColorSecondary: "#d9d9d9",
        primaryColor: "#1E6692",
        primaryColorHover: "#0C77DC",
        primaryColorLight: "#4A9EFF",
        hoverBackground: "#f0f0f0",
        selectedBackground: "#e6e6e6",
        iconColor: "#666",
        placeholderColor: "#999",
    },
    // Sentier Dark (default dark theme)
    dark: {
        backgroundColor: "#1f1f1f",
        backgroundColorSecondary: "#2d2d2d",
        backgroundColorTertiary: "#404040",
        textColor: "#ffffff",
        textColorSecondary: "#cccccc",
        textColorMuted: "#888888",
        borderColor: "#404040",
        borderColorSecondary: "#666666",
        primaryColor: "#4A9EFF",
        primaryColorHover: "#6BB3FF",
        primaryColorLight: "#6BB3FF",
        hoverBackground: "#404040",
        selectedBackground: "#1f1f1f",
        iconColor: "#ffffff",
        placeholderColor: "#888888",
    },
    // Astellas Light
    "astellas-light": {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#F5F5F5",
        backgroundColorTertiary: "#E8E8E8",
        textColor: "#333",
        textColorSecondary: "#666",
        textColorMuted: "#A7A9AC",
        borderColor: "#A7A9AC",
        borderColorSecondary: "#D0D0D0",
        primaryColor: "#D91E49",
        primaryColorHover: "#A62B4E",
        primaryColorLight: "#D91E49",
        hoverBackground: "#F5F5F5",
        selectedBackground: "#F0E6E8",
        iconColor: "#666",
        placeholderColor: "#A7A9AC",
    },
    // Astellas Dark
    "astellas-dark": {
        backgroundColor: "#1a1a1a",
        backgroundColorSecondary: "#2a2a2a",
        backgroundColorTertiary: "#3a3a3a",
        textColor: "#ffffff",
        textColorSecondary: "#E0E0E0",
        textColorMuted: "#A7A9AC",
        borderColor: "#3a3a3a",
        borderColorSecondary: "#4a4a4a",
        primaryColor: "#D91E49",
        primaryColorHover: "#A62B4E",
        primaryColorLight: "#D91E49",
        hoverBackground: "#3a3a3a",
        selectedBackground: "#2a1f22",
        iconColor: "#ffffff",
        placeholderColor: "#A7A9AC",
    },
    // Ocean Light
    "ocean-light": {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#E8F4F8",
        backgroundColorTertiary: "#D0E9F1",
        textColor: "#1a3a4a",
        textColorSecondary: "#2d5a6f",
        textColorMuted: "#5a7a8a",
        borderColor: "#5a9bb8",
        borderColorSecondary: "#A8D0E0",
        primaryColor: "#0088CC",
        primaryColorHover: "#006699",
        primaryColorLight: "#00A8E8",
        hoverBackground: "#E8F4F8",
        selectedBackground: "#D0E9F1",
        iconColor: "#2d5a6f",
        placeholderColor: "#5a7a8a",
    },
    // Ocean Dark
    "ocean-dark": {
        backgroundColor: "#0a1a24",
        backgroundColorSecondary: "#1a2a34",
        backgroundColorTertiary: "#2a3a44",
        textColor: "#E0F0F8",
        textColorSecondary: "#B0D0E8",
        textColorMuted: "#6a8a9a",
        borderColor: "#2a4a5a",
        borderColorSecondary: "#3a5a6a",
        primaryColor: "#00A8E8",
        primaryColorHover: "#00C8FF",
        primaryColorLight: "#00C8FF",
        hoverBackground: "#25465a",
        selectedBackground: "#0a1a24",
        iconColor: "#E0F0F8",
        placeholderColor: "#6a8a9a",
    },
    // Forest Light
    "forest-light": {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#F0F8F0",
        backgroundColorTertiary: "#E0F0E0",
        textColor: "#1a3a1a",
        textColorSecondary: "#2d5a2d",
        textColorMuted: "#5a7a5a",
        borderColor: "#5a9a5a",
        borderColorSecondary: "#A8D0A8",
        primaryColor: "#228B22",
        primaryColorHover: "#1a6a1a",
        primaryColorLight: "#32AB32",
        hoverBackground: "#F0F8F0",
        selectedBackground: "#E0F0E0",
        iconColor: "#2d5a2d",
        placeholderColor: "#5a7a5a",
    },
    // Forest Dark
    "forest-dark": {
        backgroundColor: "#0a1a0a",
        backgroundColorSecondary: "#1a2a1a",
        backgroundColorTertiary: "#2a3a2a",
        textColor: "#E0F0E0",
        textColorSecondary: "#B0D0B0",
        textColorMuted: "#6a8a6a",
        borderColor: "#2a4a2a",
        borderColorSecondary: "#3a5a3a",
        primaryColor: "#32AB32",
        primaryColorHover: "#42CB42",
        primaryColorLight: "#42CB42",
        hoverBackground: "#2d4830",
        selectedBackground: "#0a1a0a",
        iconColor: "#E0F0E0",
        placeholderColor: "#6a8a6a",
    },
    // High Contrast Light
    "high-contrast-light": {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#E0E0E0",
        backgroundColorTertiary: "#C0C0C0",
        textColor: "#000000",
        textColorSecondary: "#1a1a1a",
        textColorMuted: "#4a4a4a",
        borderColor: "#000000",
        borderColorSecondary: "#666666",
        primaryColor: "#0066CC",
        primaryColorHover: "#0055AA",
        primaryColorLight: "#0088FF",
        hoverBackground: "#E0E0E0",
        selectedBackground: "#C0C0C0",
        iconColor: "#000000",
        placeholderColor: "#4a4a4a",
    },
    // High Contrast Dark
    "high-contrast-dark": {
        backgroundColor: "#000000",
        backgroundColorSecondary: "#1a1a1a",
        backgroundColorTertiary: "#333333",
        textColor: "#ffffff",
        textColorSecondary: "#E0E0E0",
        textColorMuted: "#AAAAAA",
        borderColor: "#ffffff",
        borderColorSecondary: "#CCCCCC",
        primaryColor: "#0088FF",
        primaryColorHover: "#00AAFF",
        primaryColorLight: "#00AAFF",
        hoverBackground: "#2a2a2a",
        selectedBackground: "#000000",
        iconColor: "#ffffff",
        placeholderColor: "#AAAAAA",
    },
    // Purple Light
    "purple-light": {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#F5F0F8",
        backgroundColorTertiary: "#E8E0F0",
        textColor: "#2d1a3a",
        textColorSecondary: "#4a2d5a",
        textColorMuted: "#6a5a7a",
        borderColor: "#8a6aa8",
        borderColorSecondary: "#C0A8D8",
        primaryColor: "#7B2CBF",
        primaryColorHover: "#5a1a8a",
        primaryColorLight: "#9D4EDD",
        hoverBackground: "#F5F0F8",
        selectedBackground: "#E8E0F0",
        iconColor: "#4a2d5a",
        placeholderColor: "#6a5a7a",
    },
    // Purple Dark
    "purple-dark": {
        backgroundColor: "#1a0a2a",
        backgroundColorSecondary: "#2a1a3a",
        backgroundColorTertiary: "#3a2a4a",
        textColor: "#E8D0F8",
        textColorSecondary: "#C8A8E8",
        textColorMuted: "#8a6aa8",
        borderColor: "#3a2a4a",
        borderColorSecondary: "#4a3a5a",
        primaryColor: "#9D4EDD",
        primaryColorHover: "#BD6EFD",
        primaryColorLight: "#BD6EFD",
        hoverBackground: "#3d1f53",
        selectedBackground: "#1a0a2a",
        iconColor: "#E8D0F8",
        placeholderColor: "#8a6aa8",
    },
    // Amber Light
    "amber-light": {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#FFF8E8",
        backgroundColorTertiary: "#FFF0D0",
        textColor: "#3a2a1a",
        textColorSecondary: "#5a4a2a",
        textColorMuted: "#7a6a5a",
        borderColor: "#D4A85A",
        borderColorSecondary: "#E8C88A",
        primaryColor: "#F59E0B",
        primaryColorHover: "#D97706",
        primaryColorLight: "#FBBF24",
        hoverBackground: "#FFF8E8",
        selectedBackground: "#FFF0D0",
        iconColor: "#5a4a2a",
        placeholderColor: "#7a6a5a",
    },
    // Amber Dark
    "amber-dark": {
        backgroundColor: "#2a1a0a",
        backgroundColorSecondary: "#3a2a1a",
        backgroundColorTertiary: "#4a3a2a",
        textColor: "#FFF8E8",
        textColorSecondary: "#FFE8C8",
        textColorMuted: "#B4A88A",
        borderColor: "#4a3a2a",
        borderColorSecondary: "#5a4a3a",
        primaryColor: "#FBBF24",
        primaryColorHover: "#FCD34D",
        primaryColorLight: "#FCD34D",
        hoverBackground: "#4d341f",
        selectedBackground: "#2a1a0a",
        iconColor: "#FFF8E8",
        placeholderColor: "#B4A88A",
    },
    // Slate Light
    "slate-light": {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#F1F5F9",
        backgroundColorTertiary: "#E2E8F0",
        textColor: "#0F172A",
        textColorSecondary: "#334155",
        textColorMuted: "#64748B",
        borderColor: "#94A3B8",
        borderColorSecondary: "#CBD5E1",
        primaryColor: "#475569",
        primaryColorHover: "#334155",
        primaryColorLight: "#64748B",
        hoverBackground: "#F1F5F9",
        selectedBackground: "#E2E8F0",
        iconColor: "#334155",
        placeholderColor: "#64748B",
    },
    // Slate Dark
    "slate-dark": {
        backgroundColor: "#0F172A",
        backgroundColorSecondary: "#1E293B",
        backgroundColorTertiary: "#334155",
        textColor: "#F1F5F9",
        textColorSecondary: "#CBD5E1",
        textColorMuted: "#94A3B8",
        borderColor: "#334155",
        borderColorSecondary: "#475569",
        primaryColor: "#94A3B8",
        primaryColorHover: "#CBD5E1",
        primaryColorLight: "#CBD5E1",
        hoverBackground: "#2f3e56",
        selectedBackground: "#0F172A",
        iconColor: "#F1F5F9",
        placeholderColor: "#94A3B8",
    },
    // Rose Light
    "rose-light": {
        backgroundColor: "#ffffff",
        backgroundColorSecondary: "#FFF1F2",
        backgroundColorTertiary: "#FFE4E6",
        textColor: "#3a1a1a",
        textColorSecondary: "#5a2a2a",
        textColorMuted: "#7a5a5a",
        borderColor: "#FCA5A5",
        borderColorSecondary: "#FECDD3",
        primaryColor: "#E11D48",
        primaryColorHover: "#BE123C",
        primaryColorLight: "#F43F5E",
        hoverBackground: "#FFF1F2",
        selectedBackground: "#FFE4E6",
        iconColor: "#5a2a2a",
        placeholderColor: "#7a5a5a",
    },
    // Rose Dark
    "rose-dark": {
        backgroundColor: "#2a0a0a",
        backgroundColorSecondary: "#3a1a1a",
        backgroundColorTertiary: "#4a2a2a",
        textColor: "#FFF1F2",
        textColorSecondary: "#FFE4E6",
        textColorMuted: "#B48A8A",
        borderColor: "#4a2a2a",
        borderColorSecondary: "#5a3a3a",
        primaryColor: "#F43F5E",
        primaryColorHover: "#FB7185",
        primaryColorLight: "#FB7185",
        hoverBackground: "#4d2323",
        selectedBackground: "#2a0a0a",
        iconColor: "#FFF1F2",
        placeholderColor: "#B48A8A",
    },
};

// Helper hook to get theme colors
export const useThemeColors = (): ThemeColors => {
    return useAppSelector(state => {
        const theme = state.user.settings?.theme ?? (state.user.settings?.dark_mode ? "dark" : "light");
        return themeColorPalettes[theme] || themeColorPalettes.light;
    });
};

// Helper hook to get current theme name
export const useTheme = (): string => {
    return useAppSelector(state => {
        const theme = state.user.settings?.theme;
        const validThemes = [
            "dark", "light",
            "astellas-light", "astellas-dark",
            "ocean-light", "ocean-dark",
            "forest-light", "forest-dark",
            "high-contrast-light", "high-contrast-dark",
            "purple-light", "purple-dark",
            "amber-light", "amber-dark",
            "slate-light", "slate-dark",
            "rose-light", "rose-dark"
        ];
        if (theme && validThemes.includes(theme)) {
            return theme;
        }
        return state.user.settings?.dark_mode ? "dark" : "light";
    });
};

// Helper hook to get dark mode setting (supports both theme and dark_mode for backward compatibility)
export const useDarkMode = (): boolean => {
    return useAppSelector(state => {
        const theme = state.user.settings?.theme;
        const darkThemes = [
            "dark", "astellas-dark", "ocean-dark", "forest-dark", "high-contrast-dark",
            "purple-dark", "amber-dark", "slate-dark", "rose-dark"
        ];
        const lightThemes = [
            "light", "astellas-light", "ocean-light", "forest-light", "high-contrast-light",
            "purple-light", "amber-light", "slate-light", "rose-light"
        ];
        if (theme && (darkThemes.includes(theme) || lightThemes.includes(theme))) {
            return darkThemes.includes(theme);
        }
        return state.user.settings?.dark_mode ?? false;
    });
};

// Helper hook to get tooltip mode setting
export const useTooltipMode = (): "small" | "large" | "none" => {
    return useAppSelector(state => {
        const tooltipMode = state.user.settings?.tooltip_mode;
        if (tooltipMode === "small" || tooltipMode === "large" || tooltipMode === "none") {
            return tooltipMode;
        }
        return "small"; // Default to small (HTML title attributes)
    });
};
